import {useEffect, useMemo, useState} from 'react';
import useStripeConnect from '../../../../../common/useStripeConnect';
import {
  ConnectAccountManagement,
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
  ConnectNotificationBanner,
  ConnectPayouts,
} from '@stripe/react-connect-js';
import {
  fetchRequest,
  securedFetchRequest,
} from '@bidmii/common/lib/util/FetchRequest';
import * as Sentry from '@sentry/react';
import {API_URL} from '../../../../../constants';
import {useRecoilState} from 'recoil';
import {userState} from '../../../../../recoil/user/userAtom';
import PrimaryButton from '../../../../../common/StyledComponents/PrimaryButton';
import {Typography} from '@mui/material';
import useSnackbar from '../../../../../hooks/useSnackbar';
import CircularProgress from '@mui/material/CircularProgress';
import makeStyles from '@mui/styles/makeStyles';
import {theme} from '../../../../../Theme';

const useStyles = makeStyles(() => ({
  container: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    paddingBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    maxHeight: window.outerWidth > 600 ? 'calc(90vh)' : 'calc(100vh - 70px)',
    overflow: 'auto',
  },
  content: {
    flexGrow: 1,
  },
  devCallout: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(3),
    border: `1px solid ${theme.palette.warning.main}`,
  },
}));

export default function StripeContractorConnectAccount() {
  const classes = useStyles();
  const {openSnackbar} = useSnackbar();
  const [user, setUser] = useRecoilState(userState);
  const [accountCreatePending, setAccountCreatePending] = useState(false);
  const [onboardingExited, setOnboardingExited] = useState(false);
  const [connectedAccountId, setConnectedAccountId] = useState(
    user.stripeConnectAccountId,
  );
  const stripeConnectInstance = useStripeConnect(connectedAccountId);
  const [accountStatus, setAccountStatus] = useState(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  console.log('stripeConnectInstance :>> ', stripeConnectInstance);

  useEffect(() => {
    // Only fetch status if we have a connected account ID
    if (connectedAccountId) {
      setIsLoadingStatus(true);

      // Use the secured fetch request you already have
      securedFetchRequest(
        `${API_URL}/stripe-account-status/${connectedAccountId}`,
      )
        .then((res) => {
          if (!res.ok) {
            throw new Error('Failed to fetch account status');
          }
          return res.json();
        })
        .then((data) => {
          setAccountStatus(data);
        })
        .catch((err) => {
          console.error(err);
          openSnackbar(
            'Could not verify account status. Please refresh.',
            'error',
          );
          // Set to a default state that assumes onboarding is not complete
          // @ts-ignore
          setAccountStatus({requirements: {currently_due: ['_']}});
        })
        .finally(() => {
          setIsLoadingStatus(false);
        });
    } else {
      setIsLoadingStatus(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedAccountId]); // Rerun when the account ID changes

  const isOnboardingComplete = useMemo(() => {
    // @ts-ignore
    if (!accountStatus || !accountStatus.detailsSubmitted) {
      return false;
    }
    // @ts-ignore
    const isComplete =
      // @ts-ignore
      accountStatus.requirements.currentlyDue.length === 0 &&
      // @ts-ignore
      accountStatus.requirements.pastDue.length === 0;
    return isComplete;
  }, [accountStatus]);

  const requestFunction =
    process.env.REACT_APP_FORCE_WEB_VIEW === '1' ||
    (window as $TSFixMe).ReactNativeWebView
      ? fetchRequest
      : securedFetchRequest;
  const options = useMemo(() => {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    };
    if (
      process.env.REACT_APP_FORCE_WEB_VIEW === '1' ||
      (window as $TSFixMe).ReactNativeWebView
    ) {
      // @ts-expect-error
      requestOptions.headers['Firebase-Authorization'] = sessionStorage.getItem(
        'Firebase-Authorization',
      );
    }
    return requestOptions;
  }, []);

  const createConnectAccount = async () => {
    console.log('Creating Connect Account...');
    setAccountCreatePending(true);

    requestFunction(`${API_URL}/user-account`, options)
      .then((res) => res.json())
      .then((json) => {
        setAccountCreatePending(false);
        const {account, error} = json;

        if (account) {
          setConnectedAccountId(account);
          setUser({...user, stripeConnectAccountId: account});
        }

        if (error) {
          openSnackbar(error, 'error');
        }
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);
        Sentry.withScope(function (scope) {
          scope.setTag('section', 'MobileProfile.tsx toggleSaveContractor()');
          Sentry.captureException(err);
        });
      });
  };

  if (isLoadingStatus) {
    return (
      <div className="container" style={{textAlign: 'center'}}>
        <CircularProgress />
        <Typography>Verifying account status...</Typography>
      </div>
    );
  }

  if (!connectedAccountId) {
    return (
      <div className={classes.container}>
        <div className={classes.content}>
          <Typography variant="h6" gutterBottom>
            Configure your payments account:
          </Typography>
          <PrimaryButton
            onClick={createConnectAccount}
            disabled={accountCreatePending}>
            CONFIGURE
          </PrimaryButton>
          {stripeConnectInstance && (
            <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
              <ConnectAccountOnboarding
                onExit={() => setOnboardingExited(true)}
              />
            </ConnectComponentsProvider>
          )}
        </div>
      </div>
    );
  }

  // --- NEW CONDITIONAL RENDERING LOGIC ---
  return (
    <div className={classes.container}>
      <div className={classes.content}>
        {stripeConnectInstance && (
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            {isOnboardingComplete ? (
              // --- A. SHOW MANAGEMENT IF ONBOARDING IS COMPLETE ---
              <>
                <Typography variant="h6" gutterBottom>
                  Manage Your Account
                </Typography>
                <ConnectNotificationBanner />
                <ConnectAccountManagement />
                <ConnectPayouts />
              </>
            ) : (
              // --- B. FORCE ONBOARDING IF NOT COMPLETE ---
              <>
                <Typography variant="h6" gutterBottom>
                  Please complete your account setup to continue.
                </Typography>
                <ConnectAccountOnboarding
                  onExit={() => setOnboardingExited(true)}
                />
              </>
            )}
          </ConnectComponentsProvider>
        )}

        {onboardingExited && !isOnboardingComplete && (
          <div className={classes.devCallout}>
            <p>
              You have exited the setup process. Please complete the required
              steps to manage your account.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
